/**
 * Page -> Inky handoff.
 *
 * Pages offer "Ask Inky" suggestions (empty states, requirement refinement, session follow-ups).
 * They dispatch a cancelable {@link INKY_PROMPT_EVENT} window event. The Inky dock
 * (src/components/ChatAgentDock.js) listens, opens itself, places the prompt in its composer and
 * calls `event.preventDefault()` to report that it took it. By default the prompt is only
 * prefilled so the user reviews it and presses Send; pass `send: true` to start the turn at once.
 * When nothing accepts the event (signed-out pages, no dock mounted), callers fall back, for
 * example by copying the prompt so the user can paste it.
 *
 * Components inside the app shell can also call `useWorkspaceLayout().openInky({ prompt })`
 * directly; this event exists for code that should not depend on the layout context.
 *
 * Event detail: `{ prompt: string, send: boolean, context: object|null }`. The dock builds the
 * page context for the turn from the current route, so `context` is informational only.
 */

/** Window event name the Inky dock listens for. */
export const INKY_PROMPT_EVENT = 'quill:inky-prompt';

/** @deprecated Alias of {@link INKY_PROMPT_EVENT}. */
export const INKY_ASK_EVENT = INKY_PROMPT_EVENT;

/**
 * Normalize askInky arguments.
 * @param {string|{prompt: string, send?: boolean, context?: object}} input - Prompt or options.
 * @param {{send?: boolean, context?: object}} [options] - Extra options.
 * @returns {{prompt: string, send: boolean, context: object|null}} Normalized request.
 */
function normalizeRequest(input, options = {}) {
  const merged = input && typeof input === 'object' ? { ...input, ...options } : { ...options, prompt: input };
  return {
    prompt: String(merged.prompt || '').trim(),
    send: merged.send === true,
    context: merged.context && typeof merged.context === 'object' ? merged.context : null,
  };
}

/**
 * Hand a prompt to Inky.
 * @param {string|{prompt: string, send?: boolean, context?: object}} input - Prompt text (phrased
 *   as the user would type it) or an options object that includes `prompt`.
 * @param {{send?: boolean, context?: object}} [options] - `send: true` starts the turn right away;
 *   the default (false) only prefills the composer. `context` is extra page context (ids, titles).
 * @returns {boolean} True when a listener accepted the prompt.
 */
export function askInky(input, options = {}) {
  const request = normalizeRequest(input, options);
  if (!request.prompt || typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return false;
  const event = new window.CustomEvent(INKY_PROMPT_EVENT, { detail: request, cancelable: true });
  // dispatchEvent returns false when a listener called preventDefault().
  return window.dispatchEvent(event) === false;
}

/**
 * Ask Inky, or copy the prompt to the clipboard when no dock accepted it.
 * @param {string|{prompt: string, send?: boolean, context?: object}} input - Prompt text or options.
 * @param {{send?: boolean, context?: object}} [options] - Same as {@link askInky}.
 * @returns {Promise<'sent'|'prefilled'|'copied'|'unavailable'>} What happened, for a toast.
 */
export async function askInkyOrCopy(input, options = {}) {
  const request = normalizeRequest(input, options);
  if (askInky(request)) return request.send ? 'sent' : 'prefilled';
  try {
    if (request.prompt && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(request.prompt);
      return 'copied';
    }
  } catch {
    // Clipboard permission denied: fall through.
  }
  return 'unavailable';
}

/**
 * User-facing toast text for an {@link askInkyOrCopy} outcome.
 * @param {'sent'|'prefilled'|'copied'|'unavailable'} outcome - Outcome.
 * @returns {string} Message.
 */
export function describeInkyOutcome(outcome) {
  if (outcome === 'sent') return 'Inky is on it.';
  if (outcome === 'prefilled') return 'Inky is ready. Review the prompt and press Send.';
  if (outcome === 'copied') return 'Prompt copied. Paste it into Inky to continue.';
  return 'Open Inky from the side panel to ask for this.';
}
